import React, { useState, useEffect, useRef } from 'react';
import { Layout, Button, Input, Space, Card, Empty, Spin, Tag, Tooltip, message, Modal, Affix } from 'antd';
import { SendOutlined, DeleteOutlined, PlusOutlined, CopyOutlined, ThunderboltOutlined } from '@ant-design/icons';
import axios from 'axios';
import '../styles/chat.css';

const { Header, Content, Sider } = Layout;

// Relative by default so it works same-origin in production regardless of
// domain (matches the pattern used in api/client.js and JourneyPage.jsx).
// Also fixes a pre-existing bug: call sites below use paths like
// '/chat/config' (no /api prefix), so the base itself must include /api.
const API_BASE = process.env.REACT_APP_API_URL || '/api';
const API = axios.create({ baseURL: API_BASE });

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Helper to get guest ID for anonymous access
function getGuestId() {
  let guestId = localStorage.getItem('chatGuestId');
  if (!guestId) {
    guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('chatGuestId', guestId);
  }
  return guestId;
}

export default function ChatPage() {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const messagesEndRef = useRef(null);
  const [language, setLanguage] = useState('fa');

  // Scroll to bottom on new messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await API.get('/chat/config');
        setConfig(res.data.config);
      } catch (error) {
        console.error('Failed to load config:', error);
      }
    };
    fetchConfig();
  }, []);

  // Load conversations
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    setListLoading(true);
    try {
      const headers = { 'X-Guest-Id': getGuestId() };
      const res = await API.get('/chat/conversations', { headers });
      setConversations(res.data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      message.error('بارگذاری گفتگوها ناموفق بود');
    } finally {
      setListLoading(false);
    }
  };

  const loadConversation = async (id) => {
    setLoading(true);
    try {
      const headers = { 'X-Guest-Id': getGuestId() };
      const res = await API.get(`/chat/conversations/${id}`, { headers });
      setActiveConversation(res.data.conversation);
      setMessages(res.data.conversation.messages || []);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      message.error('بارگذاری گفتگو ناموفق بود');
    } finally {
      setLoading(false);
    }
  };

  const createNewConversation = async () => {
    try {
      const headers = { 'X-Guest-Id': getGuestId() };
      const res = await API.post('/chat/conversations', { language }, { headers });
      setActiveConversation(res.data.conversation);
      setMessages([]);
      setMessageInput('');
      await loadConversations();
    } catch (error) {
      console.error('Failed to create conversation:', error);
      message.error('ایجاد گفتگو جدید ناموفق بود');
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim()) return;

    const newMessage = { role: 'user', content: messageInput };
    const newMessages = [...messages, newMessage];
    setMessages(newMessages);
    setMessageInput('');
    setLoading(true);

    try {
      const headers = { 'X-Guest-Id': getGuestId() };
      const payload = {
        message: messageInput,
        language,
        conversationId: activeConversation?._id
      };

      const res = await API.post(
        activeConversation ? `/chat/conversations/${activeConversation._id}/messages` : '/chat/messages',
        payload,
        { headers }
      );

      if (res.data.conversation) {
        setActiveConversation(res.data.conversation);
        setMessages(res.data.conversation.messages);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      message.error('ارسال پیام ناموفق بود');
      // Remove the failed user message
      setMessages(newMessages.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const streamMessage = async () => {
    if (!messageInput.trim() || streaming) return;

    const userMessage = { role: 'user', content: messageInput };
    setMessages([...messages, userMessage, { role: 'assistant', content: '', isStreaming: true }]);
    setMessageInput('');
    setStreaming(true);
    setLoading(true);

    try {
      const headers = { 'X-Guest-Id': getGuestId() };
      const payload = {
        message: messageInput,
        language,
        conversationId: activeConversation?._id
      };

      const response = await API.post(
        activeConversation ? `/chat/conversations/${activeConversation._id}/stream` : '/chat/stream',
        payload,
        { headers, responseType: 'stream' }
      );

      const reader = response.data.getReader?.();
      const decoder = new TextDecoder();
      let result = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                result += data.content;
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[updated.length - 1].content = result;
                  return updated;
                });
              }
              if (data.conversation) {
                setActiveConversation(data.conversation);
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
      }

      // Remove streaming flag
      setMessages((prev) => {
        const updated = [...prev];
        delete updated[updated.length - 1].isStreaming;
        return updated;
      });
    } catch (error) {
      console.error('Streaming failed:', error);
      message.error('خطای streaming پیام');
      setMessages((prev) => prev.slice(0, -2)); // Remove user message and streaming message
    } finally {
      setStreaming(false);
      setLoading(false);
    }
  };

  const deleteConversation = (id) => {
    Modal.confirm({
      title: 'حذف گفتگو',
      content: 'این گفتگو برای همیشه حذف شود؟',
      okText: 'بله',
      cancelText: 'خیر',
      onOk: async () => {
        try {
          const headers = { 'X-Guest-Id': getGuestId() };
          await API.delete(`/chat/conversations/${id}`, { headers });
          message.success('گفتگو حذف شد');
          if (activeConversation?._id === id) {
            setActiveConversation(null);
            setMessages([]);
          }
          await loadConversations();
        } catch (error) {
          console.error('Failed to delete:', error);
          message.error('حذف ناموفق بود');
        }
      }
    });
  };

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }} dir="rtl">
      <Header className="chat-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0, color: 'white' }}>💬 چت با دامبلدور</h1>
          <Space>
            <Tag color={config?.enabled ? 'green' : 'red'}>
              {config?.enabled ? '✓ فعال' : '✗ غیرفعال'}
            </Tag>
          </Space>
        </div>
      </Header>

      <Layout style={{ height: 'calc(100vh - 64px)' }}>
        <Sider width={300} className="chat-sider" breakpoint="lg" collapsedWidth={0}>
          <div className="conversations-header">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              block
              onClick={createNewConversation}
              disabled={!config?.enabled}
            >
              گفتگو جدید
            </Button>
          </div>

          <div className="conversations-list">
            {listLoading ? (
              <Spin />
            ) : conversations.length === 0 ? (
              <Empty description="هیچ گفتگویی موجود نیست" />
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv._id}
                  className={`conversation-item ${activeConversation?._id === conv._id ? 'active' : ''}`}
                  onClick={() => loadConversation(conv._id)}
                >
                  <div className="conversation-title">{conv.title}</div>
                  <div className="conversation-meta">
                    {new Date(conv.updatedAt).toLocaleDateString('fa-IR')}
                  </div>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteConversation(conv._id);
                    }}
                  />
                </div>
              ))
            )}
          </div>
        </Sider>

        <Content className="chat-content">
          {!activeConversation ? (
            <Empty
              description="هیچ گفتگویی انتخاب‌نشده"
              style={{ marginTop: 100 }}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <>
              <div className="messages-container">
                {messages.map((msg, idx) => (
                  <div key={idx} className={`message message-${msg.role}`}>
                    <div className="message-bubble">
                      {msg.role === 'assistant' && <ThunderboltOutlined className="ai-icon" />}
                      {msg.content}
                      {msg.isStreaming && <span className="streaming-cursor">▌</span>}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="input-container">
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder="پیام خود را بنویسید..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onPressEnter={sendMessage}
                    disabled={loading || !config?.enabled}
                  />
                  <Tooltip title="ارسال (Stream)">
                    <Button
                      type="primary"
                      icon={<ThunderboltOutlined />}
                      onClick={streamMessage}
                      loading={streaming}
                      disabled={loading || !config?.enabled || !messageInput.trim()}
                    />
                  </Tooltip>
                  <Tooltip title="ارسال">
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={sendMessage}
                      loading={loading}
                      disabled={streaming || !config?.enabled || !messageInput.trim()}
                    />
                  </Tooltip>
                </Space.Compact>
              </div>
            </>
          )}
        </Content>
      </Layout>
    </Layout>
  );
}
